import { useState, useCallback, useEffect, useRef } from 'react'
import { uploadFile, getEndpointCatalog, resolveEndpointParams, syncConnector, getSyncStatus } from '../../../api'
import { connectionHealth, formatShortDate, formatShortDateTime, runStatusWord } from '../../../utils/connectionHealth'
import { classifyTables, formatTableName, lastLandedByProduct, productLabel, tableDate } from '../../../utils/dataSources'
import './PanelTabs.css'

const TONE_COLOR = {
  healthy: 'var(--green)',
  partial: 'var(--amber)',
  error: 'var(--red)',
  running: 'var(--blue, #2d5bc0)',
  never: 'var(--gray-400)',
  unknown: 'var(--gray-400)',
}

const TERMINAL = ['completed', 'completed_with_errors', 'complete', 'failed', 'error', 'interrupted']

// A RaaS connection pulls reports, not schema endpoints: its sync re-runs the report
// selection (routers/connector.start_sync's RAAS branch), so it gets no endpoint picker.
const isRaas = (c) => (c.transport || '').toUpperCase() === 'RAAS' || /RAAS/i.test(c.product_id || '')

// request() throws `API <status>: <body>`; show the route's `detail` when there is one.
function errText(e) {
  const msg = e?.message || String(e)
  const m = msg.match(/^API \d+: (.*)$/s)
  if (!m) return msg
  try { return JSON.parse(m[1]).detail || m[1] } catch { return m[1] }
}

/**
 * DataTab - the customer's data sources: connected tables, uploaded files, the saved
 * connections with their real sync state (last run, schedule), and a Pull per connection.
 *
 * Props:
 *   tables, uploads, connections, customerId, onTableClick, selectedTable,
 *   onUploadComplete, onDataChanged (reload the data sources after a pull ends),
 *   loadFailed (a list could not be read -- the page names it -- so empty is not "no data")
 */
export default function DataTab({ tables = [], uploads = [], connections = [], customerId, onTableClick, selectedTable, onUploadComplete, onDataChanged, loadFailed = false }) {
  const [pullState, setPullState] = useState({}) // connectionKey -> picker state
  const [jobs, setJobs] = useState({})           // connectionKey -> { id, status, ... }
  const [uploadState, setUploadState] = useState({ uploading: false, error: null, success: false, dragging: false })
  const pollRefs = useRef({})
  // Jobs already seen to end: a reload that still reads their history row as running
  // (its finish is written a moment after the job ends) does not start watching again.
  const endedJobs = useRef(new Set())
  useEffect(() => () => { Object.values(pollRefs.current).forEach(clearInterval) }, [])

  const { connected, uploaded, other, uploadRows } = classifyTables(tables, uploads, connections)
  const landedAt = lastLandedByProduct(tables)
  const totalRows = tables.reduce((sum, t) => sum + (t.row_count || 0), 0)

  const getConnKey = (c) => `${c.vendor_id}__${c.product_id}`
  const patchPull = (key, patch) => setPullState(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }))
  const patchJob = (key, patch) => setJobs(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }))

  // One watcher per connection: a new watch replaces the old one.
  const watchJob = useCallback((key, jobId) => {
    if (pollRefs.current[key]) clearInterval(pollRefs.current[key])
    pollRefs.current[key] = setInterval(async () => {
      try {
        const s = await getSyncStatus(jobId)
        setJobs(prev => ({ ...prev, [key]: { ...prev[key], id: jobId, ...s } }))
        if (TERMINAL.includes(s.status)) {
          clearInterval(pollRefs.current[key])
          delete pollRefs.current[key]
          endedJobs.current.add(jobId)
          if (onDataChanged) onDataChanged()
        }
      } catch (e) {
        clearInterval(pollRefs.current[key])
        delete pollRefs.current[key]
        endedJobs.current.add(jobId)
        setJobs(prev => ({ ...prev, [key]: { ...prev[key], id: jobId, status: 'lost', lostReason: errText(e) } }))
        if (onDataChanged) onDataChanged()
      }
    }, 3000)
  }, [onDataChanged])

  // A run the history says is in progress (scheduled or started elsewhere) is watched too.
  useEffect(() => {
    for (const c of connections) {
      const r = c.last_run
      const key = getConnKey(c)
      if (r?.status === 'running' && r.job_id && !pollRefs.current[key] && !endedJobs.current.has(r.job_id)) {
        setJobs(prev => (prev[key] ? prev : { ...prev, [key]: { id: r.job_id, status: 'running', started_at: r.started_at } }))
        watchJob(key, r.job_id)
      }
    }
  }, [connections, watchJob])

  const handleFileUpload = useCallback(async (file) => {
    if (!file) return
    setUploadState(s => ({ ...s, uploading: true, error: null, success: false }))
    try {
      await uploadFile(file, customerId)
      setUploadState(s => ({ ...s, uploading: false, success: true }))
      if (onUploadComplete) onUploadComplete()
      else if (onDataChanged) onDataChanged()
      else window.location.reload()
    } catch (err) {
      setUploadState(s => ({ ...s, uploading: false, error: err.message || 'Upload failed' }))
    }
  }, [customerId, onUploadComplete, onDataChanged])

  const handleInputChange = useCallback((e) => {
    handleFileUpload(e.target.files?.[0])
  }, [handleFileUpload])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setUploadState(s => ({ ...s, dragging: false }))
    const file = e.dataTransfer.files?.[0]
    if (file) handleFileUpload(file)
  }, [handleFileUpload])

  const handleDragOver = (e) => { e.preventDefault(); setUploadState(s => ({ ...s, dragging: true })) }
  const handleDragLeave = () => setUploadState(s => ({ ...s, dragging: false }))

  const handleOpenPull = async (conn) => {
    const key = getConnKey(conn)
    if (pullState[key]?.open) { patchPull(key, { open: false }); return }
    patchPull(key, { open: true, loading: !isRaas(conn), error: null, endpoints: [], selectedEndpoints: [], params: {}, resolvedParams: {} })
    if (isRaas(conn)) return
    try {
      // customer_id makes each endpoint carry its rows and last sync
      const endpoints = await getEndpointCatalog(conn.vendor_id, conn.product_id, customerId)
      patchPull(key, { loading: false, endpoints: Array.isArray(endpoints) ? endpoints : [] })
    } catch (e) {
      patchPull(key, { loading: false, error: `Couldn't load the endpoint list: ${errText(e)}` })
    }
  }

  const handleToggleEndpoint = async (conn, ep) => {
    const key = getConnKey(conn)
    const st = pullState[key] || {}
    const selected = st.selectedEndpoints || []
    const isSelected = selected.includes(ep.name)
    if (!isSelected && ep.fans_out) {
      const ok = window.confirm(
        `${ep.name} fans out: it makes one paginated call per value of its parameter, and a ` +
        'fan-out has taken production down before. The nightly sync never runs it. Pull it anyway?')
      if (!ok) return
    }
    patchPull(key, { selectedEndpoints: isSelected ? selected.filter(e => e !== ep.name) : [...selected, ep.name] })

    if (!isSelected && !(st.resolvedParams || {})[ep.name]) {
      try {
        const data = await resolveEndpointParams(customerId, ep.name, conn.vendor_id, conn.product_id)
        setPullState(prev => ({
          ...prev,
          [key]: { ...prev[key], resolvedParams: { ...(prev[key]?.resolvedParams || {}), [ep.name]: data.resolved || {} } },
        }))
      } catch { /* parameter lookup failed -- the endpoint pulls with its defaults */ }
    }
  }

  const handleParamChange = (connKey, endpointKey, paramName, value) => {
    setPullState(prev => ({
      ...prev,
      [connKey]: {
        ...prev[connKey],
        params: {
          ...(prev[connKey]?.params || {}),
          [endpointKey]: { ...(prev[connKey]?.params?.[endpointKey] || {}), [paramName]: value },
        },
      },
    }))
  }

  // all = true sends no endpoints: the sync the schedule runs (default endpoints, fan-outs
  // excluded; for RaaS the report selection with its skip list).
  const handlePull = async (conn, all) => {
    const key = getConnKey(conn)
    const st = pullState[key] || {}
    const selectedEndpoints = all ? [] : (st.selectedEndpoints || [])
    if (!all && selectedEndpoints.length === 0) return
    const endpoint_params = {}
    for (const epKey of selectedEndpoints) {
      const epParams = (st.params || {})[epKey] || {}
      if (Object.keys(epParams).length > 0) endpoint_params[epKey] = epParams
    }
    patchPull(key, { pulling: true, error: null })
    try {
      const data = await syncConnector(customerId, conn.vendor_id, conn.product_id, selectedEndpoints, endpoint_params)
      patchPull(key, { pulling: false, open: false })
      if (!data?.job_id) {
        patchJob(key, { id: null, status: 'failed', errors: [data?.message || 'The server started no job'] })
        return
      }
      const alreadyRunning = data.status === 'already_running'
      setJobs(prev => ({ ...prev, [key]: { id: data.job_id, status: 'running', alreadyRunning, message: data.message, unknown_endpoints: data.unknown_endpoints || [] } }))
      watchJob(key, data.job_id)
    } catch (e) {
      patchPull(key, { pulling: false, error: errText(e) })
    }
  }

  const tableItem = (t, extraMeta, status) => (
    <DataSourceItem key={t.table_name}
      icon={t.source_type === 'raas' ? 'RaaS' : 'API'} iconBg="var(--green)"
      name={formatTableName(t.table_name)}
      meta={[
        productLabel(t.product_id),
        `${t.column_count ?? '?'} columns`,
        `${(t.row_count || 0).toLocaleString()} rows`,
        formatShortDate(tableDate(t)),
        extraMeta,
      ].filter(Boolean).join(' · ')}
      status={status}
      onClick={onTableClick ? () => onTableClick(t.table_name, formatTableName(t.table_name)) : undefined}
      selected={selectedTable === t.table_name}
    />
  )

  return (
    <div>
      {connected.length > 0 && (
        <div className="data-section">
          <div className="data-section-title">Connected via API</div>
          {connected.map(t => tableItem(
            t,
            null,
            t.stale ? { cls: 'stale', text: 'not in last sync', title: `Last loaded ${formatShortDate(t.last_synced_at)}; its connection last synced ${formatShortDateTime(t.last_run_at)}` } : null,
          ))}
        </div>
      )}

      {(uploaded.length > 0 || uploadRows.length > 0) && (
        <div className="data-section">
          <div className="data-section-title">File Uploads</div>
          {uploaded.map(t => (
            <DataSourceItem key={t.table_name}
              icon={getFileIcon(t.upload_file)} iconBg="var(--gray-500)"
              name={formatTableName(t.table_name)}
              meta={[t.upload_file, `${(t.row_count || 0).toLocaleString()} rows`, formatShortDate(tableDate(t))].filter(Boolean).join(' · ')}
              onClick={onTableClick ? () => onTableClick(t.table_name, formatTableName(t.table_name)) : undefined}
              selected={selectedTable === t.table_name}
            />
          ))}
          {uploadRows.map(u => (
            <DataSourceItem key={`upload-${u.upload_id || u.file_name}`}
              icon={getFileIcon(u.file_name)} iconBg="var(--gray-500)"
              name={u.file_name}
              meta={`${u.rows.toLocaleString()} rows · ${formatShortDate(u.uploaded_at || u.created_at)} · no loaded table`}
              status={(u.quality_issues || []).length ? { cls: 'stale', text: `${u.quality_issues.length} issue${u.quality_issues.length !== 1 ? 's' : ''}` } : null}
            />
          ))}
        </div>
      )}

      {other.length > 0 && (
        <div className="data-section">
          <div className="data-section-title">Other tables (source not recorded)</div>
          {other.map(t => tableItem(t, null, null))}
        </div>
      )}

      {connections.length > 0 && (
        <div className="data-section">
          <div className="data-section-title">Connections</div>
          {connections.map((c) => {
            const key = getConnKey(c)
            const st = pullState[key] || {}
            const job = jobs[key]
            const health = connectionHealth(c, new Date(), { lastLandedAt: landedAt[c.product_id] })
            const jobActive = job && job.status === 'running'
            const tone = jobActive ? 'running' : health.tone
            const raas = isRaas(c)
            return (
              <div key={key} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
                  <div title={health.statusWord} style={{ width: 30, height: 30, borderRadius: 6, background: TONE_COLOR[tone], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                    {c.vendor_id?.slice(0, 3) || 'API'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--gray-900)' }}>{productLabel(c.product_id) || 'API Connection'}</div>
                    <div style={{ fontSize: 11, color: tone === 'error' ? 'var(--red)' : tone === 'partial' ? 'var(--amber)' : 'var(--gray-500)' }}>{health.line}</div>
                    {health.error && <div style={{ fontSize: 10.5, color: 'var(--gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={health.error}>{health.error}</div>}
                    {health.credentialsLine && <div style={{ fontSize: 10.5, color: 'var(--gray-400)' }}>{health.credentialsLine}</div>}
                  </div>
                  <button
                    onClick={() => handleOpenPull(c)}
                    disabled={jobActive}
                    style={{ padding: '4px 12px', fontSize: 11.5, fontWeight: 600, borderRadius: 5, border: '1px solid var(--green)', background: st.open ? 'var(--green)' : 'white', color: st.open ? 'white' : 'var(--green-dark)', cursor: jobActive ? 'not-allowed' : 'pointer', opacity: jobActive ? 0.6 : 1, whiteSpace: 'nowrap' }}
                  >
                    {st.open ? '✕ Close' : '↓ Pull Data'}
                  </button>
                </div>

                {job && <JobStatus job={job} onDismiss={jobActive ? null : () => setJobs(prev => { const n = { ...prev }; delete n[key]; return n })} />}

                {st.open && (
                  <div style={{ border: '1px solid var(--green-light)', borderTop: 'none', borderRadius: '0 0 8px 8px', background: 'var(--green-subtle)', padding: 12 }}>
                    {st.error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 8 }}>⚠ {st.error}</div>}

                    <button
                      onClick={() => handlePull(c, true)}
                      disabled={st.pulling}
                      style={{ width: '100%', padding: '7px 0', background: 'var(--green)', color: 'white', border: 'none', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: st.pulling ? 'not-allowed' : 'pointer', opacity: st.pulling ? 0.6 : 1 }}
                    >
                      {st.pulling ? 'Starting…' : '↓ Pull all (as nightly)'}
                    </button>
                    <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 4, marginBottom: raas ? 0 : 12 }}>
                      {raas
                        ? 'Re-runs the reports the nightly sync runs (its skip list honoured).'
                        : 'Every endpoint the nightly sync pulls; fan-out endpoints are left out.'}
                    </div>

                    {!raas && st.loading && <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>Loading endpoints…</div>}

                    {!raas && !st.loading && (st.endpoints || []).length > 0 && (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
                          Or pull selected endpoints
                        </div>
                        {(st.endpoints || []).map((ep) => {
                          const isChecked = (st.selectedEndpoints || []).includes(ep.name)
                          const disabled = !!ep.disabled_reason
                          const epParams = (st.resolvedParams || {})[ep.name] || {}
                          const paramValues = (st.params || {})[ep.name] || {}
                          return (
                            <div key={ep.name} style={{ marginBottom: 6, opacity: disabled ? 0.55 : 1 }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12.5, color: 'var(--gray-700)', fontWeight: isChecked ? 600 : 400 }} title={ep.disabled_reason || ep.description || ''}>
                                <input type="checkbox" checked={isChecked} disabled={disabled} onChange={() => handleToggleEndpoint(c, ep)} style={{ accentColor: 'var(--green)' }} />
                                {ep.name}
                                {ep.fans_out && <span style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 600 }}>⚠ fans out</span>}
                                {ep.row_count != null && <span style={{ fontSize: 11, color: 'var(--gray-400)', fontWeight: 400 }}>{ep.row_count.toLocaleString()} rows · {formatShortDate(ep.last_synced_at)}</span>}
                              </label>
                              {disabled && (
                                <div style={{ marginLeft: 22, fontSize: 10.5, color: 'var(--gray-500)' }}>Off: {ep.disabled_reason}</div>
                              )}

                              {isChecked && Object.keys(epParams).length > 0 && (
                                <div style={{ marginLeft: 22, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {Object.entries(epParams).map(([pName, pDef]) => (
                                    <div key={pName} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <label style={{ fontSize: 11, color: 'var(--gray-500)', minWidth: 80, flexShrink: 0 }}>{pName}</label>
                                      {pDef.options && pDef.options.length > 0 ? (
                                        <select
                                          value={paramValues[pName] || ''}
                                          onChange={e => handleParamChange(key, ep.name, pName, e.target.value)}
                                          style={{ fontSize: 11.5, padding: '3px 6px', borderRadius: 4, border: '1px solid var(--gray-300)', flex: 1, background: 'white' }}
                                        >
                                          <option value="">— select —</option>
                                          {pDef.options.map(opt => (
                                            <option key={opt.value || opt} value={opt.value || opt}>
                                              {opt.label || opt.value || opt}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <div style={{ flex: 1 }}>
                                          <input
                                            type={pDef.type === 'date' ? 'date' : 'text'}
                                            placeholder={pDef.type === 'select' ? `${pName} (type value)` : pName}
                                            value={paramValues[pName] || ''}
                                            onChange={e => handleParamChange(key, ep.name, pName, e.target.value)}
                                            style={{ fontSize: 11.5, padding: '3px 6px', borderRadius: 4, border: '1px solid var(--gray-300)', width: '100%' }}
                                          />
                                          {pDef.type === 'select' && (
                                            <div style={{ fontSize: 10, color: 'var(--amber)', marginTop: 2 }}>
                                              ⚠ No options loaded — sync company_details first, or type the ID manually
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )
                        })}

                        <button
                          onClick={() => handlePull(c, false)}
                          disabled={st.pulling || (st.selectedEndpoints || []).length === 0}
                          style={{ marginTop: 10, width: '100%', padding: '7px 0', background: 'white', color: 'var(--green-dark)', border: '1px solid var(--green)', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: (st.pulling || (st.selectedEndpoints || []).length === 0) ? 'not-allowed' : 'pointer', opacity: (st.pulling || (st.selectedEndpoints || []).length === 0) ? 0.6 : 1 }}
                        >
                          {st.pulling ? 'Starting…' : `↓ Pull ${(st.selectedEndpoints || []).length} endpoint${(st.selectedEndpoints || []).length !== 1 ? 's' : ''}`}
                        </button>
                      </>
                    )}

                    {!raas && !st.loading && (st.endpoints || []).length === 0 && !st.error && (
                      <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>No endpoints found for this connection.</div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {tables.length > 0 && (
        <div className="data-section">
          <div className="data-section-title">Summary</div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', lineHeight: 1.6 }}>
            {tables.length} tables · {totalRows.toLocaleString()} total rows
          </div>
        </div>
      )}

      {!loadFailed && tables.length === 0 && uploads.length === 0 && connections.length === 0 && (
        <div className="data-section">
          <div className="data-section-title">No Data Sources</div>
          <div style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 12 }}>Upload files or connect an API to get started.</div>
        </div>
      )}

      <div className="data-section">
        <div className="data-section-title">Upload Additional Data</div>
        <label
          className="upload-mini-zone"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          style={{ borderColor: uploadState.dragging ? 'var(--green)' : undefined, background: uploadState.dragging ? 'var(--green-subtle)' : undefined }}
        >
          {uploadState.uploading
            ? <div className="upload-mini-text">⏳ Uploading…</div>
            : uploadState.success
            ? <div className="upload-mini-text" style={{ color: 'var(--green-dark)' }}>✓ Uploaded</div>
            : uploadState.error
            ? <div className="upload-mini-text" style={{ color: 'var(--red)' }}>⚠ {uploadState.error}</div>
            : <div className="upload-mini-text">Drop files here or <strong>click to browse</strong></div>
          }
          <input type="file" accept=".xlsx,.xls,.csv,.tsv" style={{ display: 'none' }} onChange={handleInputChange} disabled={uploadState.uploading} />
        </label>
      </div>
    </div>
  )
}

// ── Helpers ──────────────────────────────────────────────

// A pull's progress and outcome, from GET /api/connector/sync-status/{job}.
function JobStatus({ job, onDismiss }) {
  let color
  let text
  const errors = job.errors || []
  if (job.status === 'running') {
    color = 'var(--blue, #2d5bc0)'
    // already_running: the conflict can be another connection's job writing the same tables
    const lead = job.alreadyRunning ? `A sync that writes these tables is already running (job ${job.id})` : `Syncing… job ${job.id}`
    const progress = job.total_tables ? ` · ${job.tables_processed || 0}/${job.total_tables} tables` : ''
    text = `${lead}${progress}${job.current_step ? ` · ${job.current_step}` : ''}`
  } else if (job.status === 'lost') {
    color = 'var(--amber)'
    text = `Lost track of job ${job.id} (${job.lostReason}). The last sync line above is reloaded from the run history.`
  } else if (job.status === 'completed' || job.status === 'complete') {
    color = errors.length ? 'var(--amber)' : 'var(--green-dark)'
    text = `Job ${job.id} ${errors.length ? 'completed with errors' : 'completed'} · ${(job.total_rows || 0).toLocaleString()} rows`
  } else {
    color = job.status === 'completed_with_errors' ? 'var(--amber)' : 'var(--red)'
    text = `Job ${job.id || ''} ${runStatusWord(job.status)}${job.total_rows ? ` · ${job.total_rows.toLocaleString()} rows` : ''}`
  }
  return (
    <div style={{ fontSize: 11.5, color, padding: '6px 10px', border: '1px solid var(--gray-200)', borderTop: 'none', borderRadius: '0 0 8px 8px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>{text}</div>
        {(job.unknown_endpoints || []).length > 0 && <div>Not endpoints of this connection, skipped: {job.unknown_endpoints.join(', ')}</div>}
        {errors.length > 0 && job.status !== 'running' && (
          <div style={{ color: 'var(--gray-500)' }} title={errors.join('\n')}>{errors.length} error{errors.length !== 1 ? 's' : ''}: {String(errors[0]).slice(0, 160)}</div>
        )}
      </div>
      {onDismiss && <button onClick={onDismiss} style={{ fontSize: 11, color: 'var(--gray-400)', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>}
    </div>
  )
}

function DataSourceItem({ icon, iconBg, name, meta, status, onClick, selected }) {
  return (
    <div
      className={`data-source-item${selected ? ' selected' : ''}${onClick ? ' clickable' : ''}`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default', marginBottom: 6 }}
    >
      <div className="data-source-icon" style={{ background: iconBg }}>{icon}</div>
      <div className="data-source-info">
        <div className="data-source-name">{name}</div>
        <div className="data-source-meta">{meta}</div>
      </div>
      {status && <span className={`data-source-status ${status.cls}`} title={status.title || ''}>{status.text}</span>}
    </div>
  )
}

function getFileIcon(fileName) {
  const ext = (fileName || '').split('.').pop().toLowerCase()
  if (ext === 'csv' || ext === 'tsv') return 'CSV'
  if (ext === 'xlsx' || ext === 'xls') return 'XLS'
  return 'FILE'
}
